import {
    useEffect,
    useMemo,
    useState,
    Fragment,
    useRef,
    useCallback,
} from "react";
import { Box, Stack, Button, Skeleton, Alert } from "@mui/material";
import { useFormikContext } from "formik";
import { useAppSelector, useSelectedClient } from "src/hooks";
import {
    authAPI,
    authSelector,
    navigatorSelector,
} from "src/modules/auth/redux/authSlice";
import { Program } from "src/modules/auth/types";
import { TripRequestFormValues } from "../types";
import { tripsAPI } from "src/modules/trips/redux/tripSlice";
import { NO_SERVICE_ERROR, ReturnTripTypes } from "../helpers";
import { MapGeoFence } from "src/components/map/GoogleMap";
import { pick } from "lodash";
import ProgramDetails from "./ProgramDetails";
import moment from "moment";
import ServiceSelector from "./ServiceSelector";

interface Props {
    label?: string;
    name?: string;
    geofence?: MapGeoFence; // passed down from location selector
    onChange?: React.ChangeEventHandler<HTMLInputElement>;
    loading?: boolean;
}
const activeCalendarFormat = "YYYY-MM-DDTHH:mm:ss";

const TripPrograms = ({
    label = "Service",
    name = "tripProgram",
    onChange,
    geofence,
    loading,
}: Props) => {
    const [open, setOpen] = useState(false);
    const { values, setFieldValue, setFieldError, validateField } =
        useFormikContext<TripRequestFormValues>();

    const { isManager, availableFundingSources, user } =
        useAppSelector(authSelector);
    const isNavigator = useAppSelector(navigatorSelector);

    const hasAddresses = useMemo(() => {
        return values.pickUpPlace.description && values.dropOffPlace.description
            ? true
            : false;
    }, [values.pickUpPlace.description, values.dropOffPlace.description]);

    // memoized to persist across rerenders
    const dateRef = useRef("");

    const {
        data: services = [],
        isLoading,
        isSuccess,
    } = tripsAPI.useGetActiveServicesQuery(dateRef.current, {
        skip: !isNavigator || !dateRef.current,
    });

    const [checkGeoEligibility, { isLoading: checkingGeoEligibility }] =
        tripsAPI.useCheckGeographicEligibiltyMutation();

    // since managers can request trips for a rider, we want to make sure we are loading
    // the riders programs instead of the managers at this point
    const client = useSelectedClient(String(values.clientID));

    const { data: onDemandUser, isLoading: loadingClient } =
        authAPI.useGetOnDemandUserQuery(client.id, {
            skip: !isNavigator,
        });

    const filterGeofence = useCallback(
        async (services: Program[]) => {
            try {
                let filtered: Program[] = [];

                if (
                    values.pickUpPlace.latitude &&
                    values.pickUpPlace.longitude
                ) {
                    const response = await checkGeoEligibility({
                        latitude: values.pickUpPlace.latitude,
                        longitude: values.pickUpPlace.longitude,
                    }).unwrap();
                    for (const geozone of response) {
                        const service = services.find((item) =>
                            item.activeGeozoneIds.includes(geozone.id)
                        );
                        if (
                            service &&
                            !filtered.find(
                                (sv) => sv.databaseID === service.databaseID
                            )
                        )
                            filtered.push(service);
                    }
                }

                if (
                    values.dropOffPlace.latitude &&
                    values.dropOffPlace.longitude
                ) {
                    const response = await checkGeoEligibility({
                        latitude: values.dropOffPlace.latitude,
                        longitude: values.dropOffPlace.longitude,
                    }).unwrap();
                    for (const geozone of response) {
                        const service = services.find((item) =>
                            item.activeGeozoneIds.includes(geozone.id)
                        );
                        if (
                            service &&
                            !filtered.find(
                                (sv) => sv.databaseID === service.databaseID
                            )
                        )
                            filtered.push(service);
                    }
                }

                return filtered.length ? filtered : services;
            } catch (error) {
                console.error("Error filtering service list:", error);
                return services;
            }
        },
        [
            checkGeoEligibility,
            values.dropOffPlace.latitude,
            values.dropOffPlace.longitude,
            values.pickUpPlace.latitude,
            values.pickUpPlace.longitude,
        ]
    );

    const [filteredPrograms, setFilteredPrograms] = useState<Program[]>([]);

    const basePrograms = useMemo(() => {
        let selected: Program[] = [];

        if (isNavigator) {
            // apply request time validations
            selected = services.reduce((acc: Program[] = [], service) => {
                // check for membership
                if (service.membershipIsRequired) {
                    const clientService = onDemandUser?.client?.programs?.find(
                        (item) => item.databaseID === service.databaseID
                    );
                    if (clientService && clientService.membershipID) {
                        acc.push(service);
                    }
                }
                // evaluate criteria exclusively to prevent duplication, services may have one or more validations that match
                else if (service.validationRequired) {
                    // check email domain match
                    if (
                        service.validDomains.includes(
                            user.Email.toLocaleLowerCase().split("@")[1]
                        )
                    ) {
                        acc.push(service);
                    } // check exact email match
                    else if (service.validEmails.includes(user.Email)) {
                        acc.push(service);
                    } // check validation code if they are both empty is still valid
                    else if (
                        service.validationCode &&
                        service.validationCode === client.validationCode
                    ) {
                        acc.push(service);
                    }
                } else {
                    // add service if validation is not
                    acc.push(service);
                }
                return acc;
            }, []);

            // sort items to prioritize those which are membership required
            selected = selected.sort((item) =>
                item.membershipIsRequired ? -1 : 1
            );
        } else if (client && client?.programs.length > 0) {
            // only select programs which are not archived
            selected = client?.programs?.filter((program) => !program.archived);
        }

        // shrink list to programs in zone with the addresses
        if (selected.length && hasAddresses) {
            if (!isNavigator && geofence && geofence.zones.length) {
                selected = selected.filter((program) => {
                    if (!program.activeGeozoneIds.length) return true;
                    return geofence.zones.some((zone) =>
                        program.activeGeozoneIds.includes(zone)
                    );
                });
            }
        }
        return selected;
    }, [
        client,
        isNavigator,
        geofence,
        hasAddresses,
        services,
        user.Email,
        onDemandUser,
    ]);

    function handleChange(evt: React.ChangeEvent<HTMLInputElement>) {
        if (isNavigator) {
            const service = filteredPrograms.find(
                (item) => item.programName === evt.target.value
            );

            // reset time if service does not support future booking
            if (service && !service.riderCanScheduleFutureTrip) {
                const newTime = moment().add(service.onDemandOffset, "m");
                setFieldValue("apptTime", newTime.toDate());
                // Ensure date is maintained when switching between future and current services with a return trip
                // @see ReturnTripOptions and TripProgram for other  implementation
                if (values.returnType !== ReturnTripTypes.oneWay) {
                    setFieldValue(
                        "returnTime",
                        moment(values.returnTime)
                            .set({
                                ...pick(newTime.toObject(), [
                                    "date",
                                    "months",
                                    "years",
                                ]),
                            })
                            .toDate()
                    );
                }
            } else {
                setFieldValue("apptTime", values.requestTime);
                // Ensure date is maintained when switching between future and current services with a return trip
                // @see ReturnTripOptions and TripProgram for other  implementation
                if (values.returnType !== ReturnTripTypes.oneWay) {
                    setFieldValue(
                        "returnTime",
                        moment(values.returnTime)
                            .set({
                                ...pick(moment(values.returnTime).toObject(), [
                                    "date",
                                    "months",
                                    "years",
                                ]),
                            })
                            .toDate()
                    );
                }
            }
        }
        // pass evt down to any other callers. Used in LocationSelector
        if (onChange) onChange(evt);
    }

    // use this hook for initialization only. Further time adjustments will be triggered by user action to limit rerenders
    useEffect(() => {
        if (!isNavigator) return;
        if (isSuccess && !services.length) {
            setFieldValue("tripProgram", "", false);
            setFieldError("tripProgram", NO_SERVICE_ERROR);
        } else if (filteredPrograms.length && !values.tripProgram) {
            // use filtered programs because they have already been adjusted by other relevant criteria
            // find default program if available and default to it

            if (client.defaultServiceId) {
                const service = filteredPrograms.find(
                    (item) => item.id === client.defaultServiceId
                );
                if (service) {
                    setFieldValue("tripProgram", service.programName);
                    setFieldValue(
                        "apptTime",
                        moment().add(service.onDemandOffset, "m").toDate(),
                        false
                    );
                }
            } else {
                setFieldValue("tripProgram", filteredPrograms[0].programName);
                setFieldValue(
                    "apptTime",
                    moment()
                        .add(filteredPrograms[0].onDemandOffset, "m")
                        .toDate(),
                    false
                );
            }
        }
    }, [
        services,
        isNavigator,
        isSuccess,
        setFieldError,
        setFieldValue,
        client.programs,
        client.defaultServiceId,
        filteredPrograms,
        values,
        onDemandUser?.client?.programs,
    ]);

    // watch apptTime to determine when to refresh all services
    useEffect(() => {
        if (!dateRef.current && values.apptTime) {
            dateRef.current = moment(values.apptTime).format(
                activeCalendarFormat
            );
        } else if (
            dateRef.current &&
            values.apptTime &&
            moment(values.apptTime).format(activeCalendarFormat) !==
                dateRef.current
        ) {
            dateRef.current = moment(values.apptTime).format(
                activeCalendarFormat
            );

            // check if currently selected service is still valid for new date or select best fit
            if (
                filteredPrograms.length &&
                !filteredPrograms.find(
                    (program) => program.programName === values.tripProgram
                )
            ) {
                setFieldValue("tripProgram", filteredPrograms[0].programName);
                validateField("apptTime");
            }
        }
    }, [
        filteredPrograms,
        setFieldValue,
        validateField,
        values.apptTime,
        values.tripProgram,
    ]);

    // watch addresses and filter down

    useEffect(() => {
        if (!hasAddresses) {
            setFilteredPrograms(basePrograms);
            return;
        }

        async function filter() {
            let selected = basePrograms;
            if (selected.length && hasAddresses) {
                if (!isNavigator && geofence && geofence.zones.length) {
                    selected = selected.filter((program) => {
                        if (!program.activeGeozoneIds.length) return true;
                        return geofence.zones.some((zone) =>
                            program.activeGeozoneIds.includes(zone)
                        );
                    });
                } else {
                    selected = await filterGeofence(selected);
                }
            }
            setFilteredPrograms(selected);
            setFieldValue(
                "tripProgram",
                selected.length
                    ? selected[0].programName
                    : basePrograms[0]?.programName || ""
            );
        }
        filter();
    }, [
        basePrograms,
        isNavigator,
        geofence,
        hasAddresses,
        filterGeofence,
        setFieldValue,
    ]);

    if (loading || isLoading || loadingClient || checkingGeoEligibility) {
        return <Skeleton width="100%" height={40} variant="rectangular" />;
    }

    if (isNavigator && isSuccess && !services.length) {
        return <Alert severity="error">{NO_SERVICE_ERROR}</Alert>;
    }

    return (
        <Fragment>
            <Stack spacing={2} direction="row" tabIndex={0}>
                <Box flexGrow={2}>
                    <ServiceSelector
                        label={label}
                        name={name}
                        programs={filteredPrograms}
                        availableFundingSources={
                            !isNavigator ? availableFundingSources : []
                        }
                        helperText={
                            hasAddresses && filteredPrograms.length
                                ? "Available services filtered due to times or locations"
                                : ""
                        }
                        onChange={handleChange}
                    />
                </Box>
                {!isManager && values.tripProgram && (
                    <Button
                        size="small"
                        variant="outlined"
                        sx={{ py: 0.5 }}
                        onClick={() => setOpen(true)}
                    >
                        View Details
                    </Button>
                )}
            </Stack>

            <ProgramDetails
                open={open}
                onClose={() => setOpen(false)}
                programs={filteredPrograms}
            />
        </Fragment>
    );
};

export default TripPrograms;
